import { _ as _type_of } from "@swc/helpers/_/_type_of";
import { checkHostContextInOffice } from './hostContextHelper';
var OFFICE_JS_SRC = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';
var LOAD_TIMEOUT_MS = 8000;
var loadPromise = null;
// Mirrors the trigger the index.html document.write loader used: in-Office signals
// (query/hash/localStorage via checkHostContextInOffice) plus the two OAuth-callback
// signals — state === 'inOffice' and state.platform of outlook/teams.
export function shouldLoadOfficeSdk() {
    if (checkHostContextInOffice()) return true;
    try {
        var state = new URLSearchParams(window.location.search).get('state');
        if (state === 'inOffice') return true;
        var parsed = state ? JSON.parse(state) : null;
        var platform = parsed && (typeof parsed === "undefined" ? "undefined" : _type_of(parsed)) === 'object' ? parsed.platform : null;
        return platform === 'outlook' || platform === 'teams';
    } catch (unused) {
        return false;
    }
}
function isOfficeReady() {
    return typeof Office !== 'undefined' && !!Office.context && !!Office.context.ui;
}
// Idempotent: injects office.js if absent and resolves once Office.onReady fires.
// Memoized so concurrent callers share one load; nulls the cache on failure so a
// later retry can re-attempt. Rejects on load error or after LOAD_TIMEOUT_MS.
export function ensureOfficeSdkLoaded() {
    if (loadPromise) return loadPromise;
    if (isOfficeReady()) {
        loadPromise = Promise.resolve();
        return loadPromise;
    }
    loadPromise = new Promise(function(resolve, reject) {
        var settled = false;
        var injectedScript = null;
        // office.js overrides history.pushState/replaceState; capture before load and
        // restore after so react-router hash routing keeps working.
        var pushStateRef = history.pushState;
        var replaceStateRef = history.replaceState;
        var timerId = setTimeout(function() {
            return fail(new Error('Office.js load timed out'));
        }, LOAD_TIMEOUT_MS);
        function succeed() {
            if (settled) return;
            settled = true;
            clearTimeout(timerId);
            resolve();
        }
        function fail(err) {
            if (settled) return;
            settled = true;
            clearTimeout(timerId);
            loadPromise = null;
            // Drop the dead script so a later retry injects a clean one.
            if (injectedScript && injectedScript.parentNode) {
                injectedScript.parentNode.removeChild(injectedScript);
            }
            reject(err);
        }
        var waitForReady = function waitForReady() {
            history.pushState = pushStateRef;
            history.replaceState = replaceStateRef;
            if (typeof Office !== 'undefined' && typeof Office.onReady === 'function') {
                Office.initialize = function() {
                    return undefined;
                };
                Office.onReady(function() {
                    return succeed();
                });
            } else {
                fail(new Error('Office.js loaded but Office.onReady is unavailable'));
            }
        };
        var existing = document.querySelector('script[src="'.concat(OFFICE_JS_SRC, '"]'));
        if (existing) {
            if (isOfficeReady()) {
                waitForReady();
            } else {
                existing.addEventListener('load', waitForReady);
                existing.addEventListener('error', function() {
                    return fail(new Error('Failed to load Office.js'));
                });
            }
        } else {
            var script = document.createElement('script');
            script.src = OFFICE_JS_SRC;
            script.async = true;
            injectedScript = script;
            script.onload = waitForReady;
            script.onerror = function() {
                return fail(new Error('Failed to load Office.js'));
            };
            document.head.appendChild(script);
        }
    });
    return loadPromise;
}
